import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { useT } from '../i18n';
import { Icon, Menu, Modal, initials, useConfirm, useToast, STORE_LINKS } from '../components/ui';
import { updateUser } from '../lib/services';
import { xpState } from '../lib/xp';
import { XpFrame, XpMenuHeader, XpSidebarCard, XpTitlesModal } from '../components/xp';

export function GlowBackground({ photo }) {
  const { backgrounds } = useSession() || { backgrounds: true };
  if (!backgrounds) return null;
  return (
    <div className="gm-bg" aria-hidden>
      {photo && <div className="gm-bg__photo" style={{ backgroundImage: `url(${photo})` }} />}
      <div className="gm-bg__dim" />
      <div className="gm-bg__blob gm-bg__blob--a" />
      <div className="gm-bg__blob gm-bg__blob--b" />
    </div>
  );
}

const TRAINEE_NAV = [
  { to: '/home', icon: 'home', key: 'home' },
  { to: '/program', icon: 'fitness_center', key: 'programs' },
  { to: '/nutrition', icon: 'restaurant', key: 'nutriAiTab' },
  { to: '/progress', icon: 'show_chart', key: 'progress' },
  { to: '/ai-trainer', icon: 'smart_toy', key: 'aiTrainer' },
];

const TRAINER_NAV = [
  { to: '/home', icon: 'home', key: 'home' },
  { to: '/trainees', icon: 'groups', key: 'trainees' },
];

const PAGE_TITLES = {
  '/home': 'appTitle',
  '/program': 'weeklyProgramTitle',
  '/nutrition': 'nutriAiTab',
  '/nutrition/records': 'allRecords',
  '/progress': 'bodyProgressTitle',
  '/progress/records': 'allRecords',
  '/ai-trainer': 'aiTrainer',
  '/trainees': 'trainees',
  '/profile': 'profile',
  '/settings': 'settings',
  '/admin': 'adminPanel',
  '/badges': 'badgesPageTitle',
  '/weekly-summary': 'weeklySummaryPageTitle',
};

const BG_PHOTOS = {
  '/home': '/assets/backgrounds/home-scroll-background.jpg',
  '/nutrition': '/assets/backgrounds/nutrition-scroll-background.jpg',
};

export function PremiumModal({ open, onClose }) {
  const t = useT();
  const features = [
    ['bolt', 'premiumAdvantage1'],
    ['block', 'premiumAdvantage2'],
    ['insights', 'premiumAdvantage3'],
    ['support_agent', 'premiumAdvantage4'],
    ['rocket_launch', 'premiumAdvantage5'],
  ];
  return (
    <Modal open={open} onClose={onClose} title={null}>
      <div className="gm-row" style={{ marginBottom: 8 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--gm-premium-a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <Icon name="star" />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gm-premium-a)', letterSpacing: 0.6 }}>GymGM</div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>{t('getPremiumTitle')}</div>
        </div>
      </div>
      <p className="gm-muted" style={{ lineHeight: 1.6 }}>{t('getPremiumSubtitle')}</p>
      <div className="gm-stack-sm" style={{ margin: '22px 0' }}>
        {features.map(([icon, key]) => (
          <div key={key} className="gm-row" style={{ padding: '12px 14px', borderRadius: 12, background: 'var(--gm-chip)' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'color-mix(in srgb, var(--gm-premium-a) 18%, transparent)', color: 'var(--gm-premium-a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={icon} size={18} />
            </div>
            <span className="gm-strong">{t(key)}</span>
          </div>
        ))}
      </div>
      <p className="gm-small gm-muted" style={{ marginBottom: 12, textAlign: 'center' }}>{t('planManagedInApp')}</p>
      <div className="gm-row gm-wrap" style={{ justifyContent: 'center' }}>
        <a className="gm-btn gm-btn--premium" href={STORE_LINKS.play} target="_blank" rel="noreferrer">
          <Icon name="shop" /> {t('openPlayStore')}
        </a>
        {STORE_LINKS.ios && (
          <a className="gm-btn gm-btn--premium" href={STORE_LINKS.ios} target="_blank" rel="noreferrer">
            <Icon name="phone_iphone" /> {t('openAppStore')}
          </a>
        )}
      </div>
    </Modal>
  );
}

export default function AppShell() {
  const t = useT();
  const { user, signOut, isAdmin } = useSession();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [premiumOpen, setPremiumOpen] = useState(false);
  const [titlesOpen, setTitlesOpen] = useState(false);
  // XP is a trainee feature, like in the app.
  const xp = user.isTrainer ? null : xpState(user);

  const nav = user.isTrainer ? TRAINER_NAV : TRAINEE_NAV;
  const titleKey = PAGE_TITLES[location.pathname] || 'appTitle';

  useEffect(() => {
    const title = `GymGM - ${t(titleKey)}`;
    document.title = title;
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: `/web/app${location.pathname}`, page_title: `Web - ${title}` });
    }
  }, [location.pathname, titleKey, t]);

  const switchRole = async () => {
    const toTrainee = user.isTrainer;
    const ok = await confirm({
      title: t(toTrainee ? 'switchRoleToTraineeTitle' : 'switchRoleToTrainerTitle'),
      message: t(toTrainee ? 'switchRoleToTraineeMessage' : 'switchRoleToTrainerMessage'),
    });
    if (!ok) return;
    try {
      await updateUser(user.id, { role: toTrainee ? 'trainee' : 'trainer' });
      navigate('/home');
    } catch (e) {
      toast.error(t('anErrorOccurred'));
    }
  };

  const logout = async () => {
    const ok = await confirm({ title: t('signOut'), message: t('signOutConfirm'), confirmLabel: t('signOut') });
    if (ok) {
      await signOut();
      navigate('/');
    }
  };

  const avatarMenu = (
    <Menu
      trigger={({ toggle }) => {
        const avatar = <button type="button" className="gm-avatar" onClick={toggle} title={t('profile')}>{initials(user)}</button>;
        return xp ? <XpFrame state={xp}>{avatar}</XpFrame> : avatar;
      }}
    >
      {xp && (
        <>
          <XpMenuHeader state={xp} onOpen={() => setTitlesOpen(true)} />
          <hr />
        </>
      )}
      <div className="gm-menu__head">
        <div className="gm-strong">{`${user.firstName} ${user.lastName}`.trim() || user.email}</div>
        <div className="gm-small gm-muted">{user.email}</div>
      </div>
      <hr />
      <button type="button" onClick={() => navigate('/profile')}><Icon name="person" size={20} /> {t('profile')}</button>
      <button type="button" onClick={() => navigate('/settings')}><Icon name="settings" size={20} /> {t('settings')}</button>
      {isAdmin && <button type="button" onClick={() => navigate('/admin')}><Icon name="admin_panel_settings" size={20} /> {t('adminPanel')}</button>}
      <button type="button" onClick={switchRole}><Icon name="swap_horiz" size={20} /> {user.isTrainer ? t('switchRoleToTraineeTitle') : t('switchRoleToTrainerTitle')}</button>
      <hr />
      <button type="button" className="danger" onClick={logout}><Icon name="logout" size={20} /> {t('signOut')}</button>
    </Menu>
  );

  return (
    <div className="gm">
      <GlowBackground photo={BG_PHOTOS[location.pathname]} />
      <div className="gm-shell">
        <aside className="gm-sidebar">
          <NavLink to="/home" className="gm-brand">
            <img src="/gymgm_logo.jpg" alt="" />
            <span>GYM<b>GM</b></span>
          </NavLink>
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `gm-nav-item${isActive ? ' active' : ''}`}>
              <Icon name={n.icon} size={21} /> {t.upper(t(n.key))}
            </NavLink>
          ))}
          <div className="gm-sidebar__section">{t.upper(t('userInformation'))}</div>
          <NavLink to="/profile" className={({ isActive }) => `gm-nav-item${isActive ? ' active' : ''}`}>
            <Icon name="person" size={21} /> {t.upper(t('profile'))}
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => `gm-nav-item${isActive ? ' active' : ''}`}>
            <Icon name="settings" size={21} /> {t.upper(t('settings'))}
          </NavLink>
          {isAdmin && (
            <NavLink to="/admin" className={({ isActive }) => `gm-nav-item${isActive ? ' active' : ''}`}>
              <Icon name="admin_panel_settings" size={21} /> {t.upper(t('adminPanel'))}
            </NavLink>
          )}
          <button type="button" className="gm-nav-item gm-nav-item--btn" onClick={switchRole}>
            <Icon name="swap_horiz" size={21} /> {t.upper(user.isTrainer ? t('switchRoleToTraineeTitle') : t('switchRoleToTrainerTitle'))}
          </button>
          <button type="button" className="gm-nav-item gm-nav-item--btn" onClick={logout}>
            <Icon name="logout" size={21} /> {t.upper(t('signOut'))}
          </button>
          <div className="gm-sidebar__spacer" />
          {xp && <XpSidebarCard state={xp} onOpen={() => setTitlesOpen(true)} />}
          {!user.isPremium && (
            <button type="button" className="gm-btn gm-btn--premium gm-btn--block" onClick={() => setPremiumOpen(true)}>
              <Icon name="workspace_premium" /> {t('upgradeToPremium')}
            </button>
          )}
          <div className="gm-row" style={{ padding: '10px 6px 0' }}>
            <span className="gm-pill">{user.isTrainer ? t('trainer') : t('trainee')}</span>
            {user.isPremium && <span className="gm-pill" style={{ color: 'var(--gm-premium-b)' }}>{t('premiumPlan')}</span>}
          </div>
        </aside>

        <div className="gm-main">
          <header className="gm-topbar">
            {!user.isPremium && (
              <button type="button" className="gm-premium-chip" onClick={() => setPremiumOpen(true)} title={t('upgradeToPremium')}>
                <Icon name="workspace_premium" />
              </button>
            )}
            <div className="gm-topbar__title">
              <span className="gm-brand-mini">{t.upper(t('appTitle'))}</span>
            </div>
            <div className="gm-topbar__actions">
              {avatarMenu}
            </div>
          </header>
          <main>
            <Outlet context={{ openPremium: () => setPremiumOpen(true) }} />
          </main>
        </div>
      </div>

      <nav className="gm-bottomnav">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? 'active' : '')}>
            <Icon name={n.icon} size={22} />
            {t.upper(t(n.key))}
          </NavLink>
        ))}
      </nav>

      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />
      {xp && <XpTitlesModal open={titlesOpen} onClose={() => setTitlesOpen(false)} state={xp} />}
    </div>
  );
}
